import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  APP_VERSION,
} from 'generated/version';
import {
  isDev,
  isProdOrQA,
} from 'env';
import isNil from 'lodash/isNil';

type VersionInfo = {
  version?: string;
  buildTime?: string;
};

export async function fetchAppVersionInfo(): Promise<VersionInfo | null> {
  try {
    const response = await fetch('/version.json', {
      cache: 'no-store',
    });

    if (!response.ok) {
      return null;
    }

    return await response.json() as VersionInfo;
  } catch {
    return null;
  }
}

const DEV_DEFAULT_INTERVAL_MS = 60 * 1000;
const PROD_DEFAULT_INTERVAL_MS = 5 * 60 * 1000;

const DEFAULT_INTERVAL_MS = isDev
  ? DEV_DEFAULT_INTERVAL_MS
  : PROD_DEFAULT_INTERVAL_MS;

export default function useAppVersionCheck(intervalMs = DEFAULT_INTERVAL_MS) {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [latestVersion, setLatestVersion] = useState<string | undefined>();
  const checkingRef = useRef(false);

  const reloadApp = useCallback(() => {
    window.location.reload();
  }, []);

  const checkVersion = useCallback(async () => {
    if (!isProdOrQA || checkingRef.current) {
      return;
    }

    checkingRef.current = true;

    try {
      const latestVersionInfo = await fetchAppVersionInfo();

      if (!isNil(latestVersionInfo?.version) && latestVersionInfo.version !== APP_VERSION) {
        setLatestVersion(latestVersionInfo.version);
        setUpdateAvailable(true);
        console.warn('New version available', latestVersionInfo.version);
      }
    } finally {
      checkingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!isProdOrQA) {
      return undefined;
    }

    checkVersion();

    const intervalId = window.setInterval(checkVersion, intervalMs);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [checkVersion, intervalMs]);

  return {
    updateAvailable,
    latestVersion,
    reloadApp,
  };
}
